import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import logo from "../../images/logo.png";

const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];

const validateFile = (file) => {
  if (!file) return null;
  if (!allowedTypes.includes(file.type)) {
    alert("Only PDF, JPG, or PNG files are allowed.");
    return null;
  }
  return file;
};

const TaxForm = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const [isSigned, setIsSigned] = useState(false);
  const [taxFile, setTaxFile] = useState(null);
  const [files, setFiles] = useState([]);

  // 🔄 Fetch uploaded tax form
  const fetchTaxDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/taxform/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setFiles(res.data || []);
    } catch (err) {
      console.error("Fetch tax docs failed", err);
    }
  }, [API_BASE, userId]);

  useEffect(() => {
    fetchTaxDocs();
  }, [fetchTaxDocs]);

  // 🔄 Signature status (UNCHANGED)
  useEffect(() => {
    const fetchTaskStatus = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/usertasks/status/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const TASK_ID = 5;
        const idKey = String(TASK_ID);
        const nameKey = "tax_form";

        const status =
          res.data.status?.[idKey] ??
          res.data.status?.[nameKey] ??
          "not_completed";

        setIsSigned(status === "review" || status === "completed");
      } catch (err) {
        console.error("Error checking signature status:", err);
      }
    };

    if (userId) fetchTaskStatus();
  }, [userId, API_BASE]);

  // ⬆ Upload
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!taxFile) {
      alert("Please select a file to upload.");
      return;
    }

    const formData = new FormData();
    formData.append("tax_form", taxFile);
    formData.append("userId", userId);

    try {
      await axios.post(
        `${API_BASE}/api/taxform/upload?userId=${userId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      alert("Upload successful!");
      setTaxFile(null);
      fetchTaxDocs();
    } catch (err) {
      console.error("Upload failed", err);
      alert("Upload failed");
    }
  };

  const handleCancel = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  const uploadedForm = files.find((f) => f.tax_form_type === "W4");

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>Tax Form</span>
        <button className="btn btn-secondary" onClick={handleCancel}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Tax Form</h2>
          </div>
          <CompanyAddress />
        </div>
        <p>
          CCRA link for the forms:{" "}
          <a
            href="https://www.canada.ca/en/revenue-agency/services/forms-publications/td1-personal-tax-credits-returns/td1-forms-pay-received-on-january-1-later.html"
            target="_blank"
            rel="noreferrer"
          >
            TD1 Forms Reference
          </a>
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Upload Tax Form</label>
            <input
              type="file"
              className="form-control"
              disabled={!isAdmin && isSigned}
              onChange={(e) => setTaxFile(validateFile(e.target.files[0]))}
            />
          </div>

          <div className="d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!taxFile || (!isAdmin && isSigned)}
            >
              Upload
            </button>
          </div>
        </form>

        {uploadedForm && (
          <div className="mt-4 p-3 bg-light rounded border">
            <div className="text-muted mb-2">Uploaded Version</div>
            <a
              href={`${API_BASE}/uploads/taxforms/${uploadedForm.tax_file_path}`}
              target="_blank"
              rel="noreferrer"
            >
              {uploadedForm.tax_original_name}
            </a>
          </div>
        )}

        {userId && (
          <>
            <SignatureManager
              taskName="tax_form"
              taskId={5}
              userId={userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="tax_form"
              taskId={5}
              userId={userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default TaxForm;
